import isEqual from 'react-fast-compare';
import {createSelector} from 'reselect';
import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_NETATMO_WEATHER,
  SOURCE_SYNOP,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
} from '../../api';
import {StationSource} from '../../api/types';
import {Forecast, Station} from '../../utils';
import {NormalizedForecastModel} from '../../utils/Forecast/types';
import {selectIsPremium} from '../billing/selectors';
import {deriveForecastSelection} from '../forecastModel/deriveForecastSelection';
import {resolveSelectedForecastModel} from '../forecastModel/resolveSelectedForecastModel';
import {CommonRootState} from '../types';
import {
  selectForecastModelMode,
  selectForecastModelOrder,
  selectForecastModelPins,
  selectForecastModelStrategy,
} from '../userPreference/selectors';

const EMPTY_DASHBOARD_ID_LIST: string[] = [];
const EMPTY_DASHBOARD_CONFIG: NonNullable<
  CommonRootState['weather']['dashboardConfig']
> = {};
const DEFAULT_MAP_SOURCE_FILTER: StationSource[] = [
  SOURCE_SYNOP,
  SOURCE_NETATMO_WEATHER,
  SOURCE_DAVIS,
  SOURCE_WUNDERGROUND,
  SOURCE_AMBIENT_WEATHER,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_WEATHERFLOW,
];
const EMPTY_FORECAST_MODEL_LIST: NormalizedForecastModel[] = [];

export const getStationList = (state: CommonRootState) =>
  state.weather.stationList;

const getStationSort = (state: CommonRootState) => state.weather.stationSort;

export const getSortedStationList = createSelector(
  getStationList,
  getStationSort,
  (stationList, stationSort) =>
    stationList
      .slice()
      .sort((a, b) => stationSort.indexOf(a.id) - stationSort.indexOf(b.id)),
);

export const getSortedVisibleStationList = createSelector(
  getSortedStationList,
  (sortedStationList) =>
    sortedStationList.filter((station) => !Station.isStationHidden(station)),
);

export const selectFavoriteStationIds = (state: CommonRootState) =>
  state.weather.favoriteStationIds;

/*
 * Retrieve ids from both stationList and favoriteIdList
 */
export const getUserStationIds = createSelector(
  getStationList,
  selectFavoriteStationIds,
  (stationList, favoriteIds) => {
    const fullList = stationList.map((s) => s.id).concat(favoriteIds);
    return fullList.filter((item, index) => fullList.indexOf(item) === index);
  },
);

export const getRawCurrentStationId = (state: CommonRootState) =>
  state.weather.currentStation;

export const getCurrentPublicStationId = createSelector(
  getRawCurrentStationId,
  selectFavoriteStationIds,
  getSortedStationList,
  (stationId, favoriteIds, stationList) =>
    stationId ||
    (favoriteIds.length > 0
      ? favoriteIds[0]
      : stationList.length > 0
        ? stationList[0].id
        : null),
);

export const getCurrentPublicStationFromList = createSelector(
  getCurrentPublicStationId,
  getStationList,
  (currentStationId, stationList) => {
    if (currentStationId) {
      return stationList.find((s) => s.id === currentStationId) || null;
    }
    return null;
  },
  // Keep a stable reference across polls that return identical content (#41),
  // so the dashboard station object — and the chart `data` it feeds — does not
  // change identity and force a Reanimated worklet re-serialization.
  {memoizeOptions: {resultEqualityCheck: isEqual}},
);

export const getId = (_: CommonRootState, id: number) => id;

export const getPublicStationFromList = createSelector(
  getStationList,
  getId,
  (stationList, id) => {
    if (id) {
      return stationList.find((s) => s.id === id) || null;
    }
    return null;
  },
);

export const getStationMapSourceFilter = (state: CommonRootState) =>
  (state.weather.stationMapFilter && state.weather.stationMapFilter.source) ||
  DEFAULT_MAP_SOURCE_FILTER;

export const getMapMetric = (state: CommonRootState) =>
  (state.weather.stationMapFilter && state.weather.stationMapFilter.metric) ||
  'T';

export const selectWeatherChartConfig = (state: CommonRootState) =>
  state.weather.chartConfig;

/*
Dashboard Customization
*/
export const selectDashboardConfig = (state: CommonRootState) =>
  state.weather.dashboardConfig || EMPTY_DASHBOARD_CONFIG;

export const getDashboardOrder = createSelector(
  selectDashboardConfig,
  getId,
  (config, id) => (config[id] ? config[id].order : EMPTY_DASHBOARD_ID_LIST),
);

export const getDashboardHidden = createSelector(
  selectDashboardConfig,
  getId,
  (config, id) => (config[id] ? config[id].hidden : EMPTY_DASHBOARD_ID_LIST),
);

/*
Weather Forecast
*/
const getRawForecastModels = (state: CommonRootState) => {
  return state.weather.forecastModels;
};

export const getForecastModelsByGenericStationId = createSelector(
  getRawForecastModels,
  selectForecastModelOrder,
  getId,
  (forecastModels, savedOrder, id) => {
    if (id in forecastModels) {
      const list = Forecast.ForecastUtils.getForecastModelList(
        forecastModels[id],
      ).getList();
      return Forecast.ForecastUtils.orderForecastModelsByPreference(
        list,
        savedOrder,
      );
    }
    return EMPTY_FORECAST_MODEL_LIST;
  },
);

const getRawSelectedForecastModel = (state: CommonRootState) => {
  return state.weather.selectedForecastModel;
};

/**
 * Stands in for the override dictionary when resolving what the *preference*
 * alone would serve. Shared and frozen so the resolve is fed one stable empty
 * object rather than a fresh literal per call.
 */
const NO_OVERRIDES: Record<number, string> = Object.freeze({});

export const getSelectedForecastModelByGenericStationId = createSelector(
  getForecastModelsByGenericStationId,
  getRawSelectedForecastModel,
  selectForecastModelOrder,
  getId,
  selectIsPremium,
  resolveSelectedForecastModel,
);

/**
 * Why this station shows the model it shows — see `deriveForecastSelection`.
 *
 * The picker needs the reason, not just the model: `fallback` and
 * `noPreference` resolve identically today, so a screen comparing models
 * cannot tell "your preference did not reach here" from "you have no
 * preference". Derived here rather than in each app so both branch on one
 * answer.
 */
export const getForecastSelectionByGenericStationId = createSelector(
  getSelectedForecastModelByGenericStationId,
  getForecastModelsByGenericStationId,
  getRawSelectedForecastModel,
  getId,
  selectForecastModelPins,
  selectForecastModelMode,
  selectForecastModelStrategy,
  selectIsPremium,
  selectForecastModelOrder,
  (resolved, list, overrides, id, pins, mode, strategy, isPremium, order) =>
    deriveForecastSelection({
      resolved,
      // The same resolve, with the override dictionary emptied: what the
      // profile preference alone serves here. Cheap — a walk over at most a
      // dozen models — and there is no other way to name what "use my
      // preferred models" would give you while an override is in force.
      preferred: resolveSelectedForecastModel(
        list,
        NO_OVERRIDES,
        order,
        id,
        isPremium,
      ),
      isOverridden: id in overrides,
      pins,
      available: list.map((item) => item.serverId.forecastModel),
      hasPreference: Forecast.hasForecastPreference(pins, mode, strategy),
      // The same predicate the resolver two selectors up is fed. Anything else
      // lets the strip describe a model the resolver refuses to serve.
      isPremium,
    }),
);
